/*
 *   File : user-registration.js
 *   Author : https://evoqins.com
 *   Description : User register component
 *   Integrations : NA
 *   Version : 1.0.0
*/

import React, { useEffect, useRef, useState } from 'react';

//Custom Component
import { DataTableComponent } from '../../../Component/DataTable';
import { CustomSelectBox } from '../../../Component/FormElements';
import { SummaryCard } from '../../../Component/Cards';
import { APIService } from '../../../Service';
import { Loader } from '../../../Component/Others';
import { EmptyScreen } from '../../../Component/EmptyComponent';

const USER_REG_FILTER = [
    {
        value: 1,
        label: 'Latest user registered',
    },
    {
        value: 2,
        label: 'By top earned'
    },
    {
        value: 3,
        label: 'By sales performance'
    },
];

const ITEMS_PER_PAGE = 15;

const PAGE_SORT_FIELDS = [
    'customer_id',
    'customer_name',
    'email',
    'store_name',
    'contact_no',
    'reward',
    'total_sales',
];

const UserRegistered = (props) => {

    const tableRef = useRef(null);
    const isSortingRef = useRef(false);
    const skipListFetchRef = useRef(false);

    const [selected, setSelected] = useState(USER_REG_FILTER[0]);
    const [toggleFilter, setToggleFilter] = useState(false);

    const [userRegisteredData, setUserRegisteredData] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const [selectedCity, setSelectedCity] = useState({ value: null, label: "All" });

    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    useEffect(() => {
        if (skipListFetchRef.current) {
            skipListFetchRef.current = false;
            return;
        }
        _userRegisteredData();
    }, [selected, selectedCity, currentPage]);

    // handle filter registered user 
    const _handleFilterRegister = (value) => {
        setSelected(value);
        setLoading(true);
        setSortField(null);
        setSortDirection(null);
        skipListFetchRef.current = true;
        setCurrentPage(1);
        _userRegisteredData(1, null, null, value.value, selectedCity.value);
    };

    // handle filter city 
    const _handleFilterCity = (value) => {
        setSelectedCity(value);
        setLoading(true);
        setSortField(null);
        setSortDirection(null);
        skipListFetchRef.current = true;
        setCurrentPage(1);
        _userRegisteredData(1, null, null, selected.value, value.value);
    };

    // handle page change of table
    const _handlePageChange = (page_num) => {
        if (isSortingRef.current) {
            isSortingRef.current = false;
            return;
        }

        if (page_num === currentPage) {
            return;
        }

        setCurrentPage(page_num);
        setLoading(true);

        if (tableRef.current) {
            tableRef.current.scrollIntoView({ behavior: 'smooth', block: "start" });
        }
    };

    const _handleSort = (column, direction) => {
        const sortBy = column.sortField || column.id || null;

        if (!sortBy || !PAGE_SORT_FIELDS.includes(sortBy)) {
            return;
        }

        isSortingRef.current = true;
        setSortField(sortBy);
        setSortDirection(direction);
        setLoading(true);

        // Only skip the list useEffect when page will actually change.
        if (currentPage !== 1) {
            skipListFetchRef.current = true;
        }
        setCurrentPage(1);
        _userRegisteredData(1, sortBy, direction);

        setTimeout(() => {
            isSortingRef.current = false;
        }, 0);
    };

    // user registered table 
    const TABLE_COLUMNS = [
        {
            name: 'Users name',
            id: 'customer_name',
            sortField: 'customer_name',
            selector: row => row.customer_name,
            width: "220px",
            sortable: true,
        },
        {
            name: 'Shop',
            id: 'store_name',
            sortField: 'store_name',
            selector: row => row.store_name,
            width: "260px",
            sortable: true,
        },
        {
            name: 'Email',
            id: 'email',
            sortField: 'email',
            cell: (row) =>
                row.email === null ?
                    "-"
                    :
                    <a href={`mailto:${row.email}`} className='e-text-rich-black text-decoration-hover-underline text-decoration-none cursor-pointer'>
                        {row.email}
                    </a>,
            width: "200px",
            sortable: true,
        },
        {
            name: 'Contact no',
            id: 'contact_no',
            sortField: 'contact_no',
            cell: (row) =>
                row.contact_no === null ?
                    "-"
                    :
                    <a href={`tel:${row.contact_no}`} className='e-text-rich-black text-decoration-hover-underline text-decoration-none cursor-pointer'>
                        {row.contact_no}
                    </a>,
            width: "160px",
            sortable: true,
        },
        {
            name: 'Reward points earned',
            id: 'reward',
            sortField: 'reward',
            selector: row => row.reward,
            width: "200px",
            sortable: true,
        },
        {
            name: (
                <div className='d-flex'>
                    <span className='pe-1'>Total sales</span>
                    <div className='e-tooltip'>
                        <img src={require("../../../Assets/Image/note.svg").default}
                            draggable={false}
                            alt='tooltip' />
                        <p className="mb-0 px-2 padding-12px-tb e-tooltip-content e-box-tooltip-box-shadow e-text-rich-black e-alt-font-noto-sans e-font-12 e-font-weight-400 line-height-16px">
                            Both premium and normal sales
                        </p>
                    </div>
                </div>
            ),
            id: 'total_sales',
            sortField: 'total_sales',
            selector: row => row.total_sales,
            width: "160px",
            sortable: true,
        }
    ];

    // API - Get registered user data
    const _userRegisteredData = (
        page_num = currentPage,
        sort_by = sortField,
        sort_order = sortDirection,
        filter = selected.value,
        city_id = selectedCity.value
    ) => {
        const effective_sort_by = sort_by && PAGE_SORT_FIELDS.includes(sort_by) ? sort_by : null;
        const effective_sort_order = effective_sort_by ? sort_order : null;

        let is_post = true;
        let url = 'country-admin/user-registered';
        let data = {
            "country_admin_id": props.adminId,
            "city_id": city_id,
            "page_num": page_num,
            "filter": filter,
            "limit": ITEMS_PER_PAGE,
            "sort_by": effective_sort_by ? [effective_sort_by] : null,
            "order": effective_sort_order ? [effective_sort_order] : null,
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setUserRegisteredData(response.data);
            } else { }
            setLoading(false);
        });
    };

    return (
        <>
            {loading ?
                <div className='margin-20px-top e-bg-white e-admin-box-shadow py-4 e-border-radius-16 '>
                    {/*loader */}
                    <Loader />
                </div>
                :
                <>
                    {/* Summary */}
                    <div className='e-admin-box-shadow p-4 e-bg-white e-border-radius-16'>
                        <h4 className='mb-0 e-text-rich-black e-alt-font-noto-sans e-font-20 e-font-weight-500 line-height-28px'>
                            Summary
                        </h4>

                        {/* Summary card */}
                        <div className='d-flex padding-10px-top gap-0-24px'>
                            <SummaryCard title="Registered sales specialists"
                                quantity={userRegisteredData.registered_user} />
                            <SummaryCard title="Blocked sales specialists"
                                quantity={userRegisteredData.blocked_count} />
                        </div>
                    </div>

                    {/* Sales specialists table */}
                    <div ref={tableRef} className='margin-20px-top e-bg-white e-admin-box-shadow py-4 e-border-radius-16 '>
                        <div className='d-flex px-4 justify-content-space-between'>
                            <h4 className='mb-0 e-text-rich-black e-alt-font-noto-sans e-font-20 e-font-weight-600 line-height-28px'>
                                Sales specialists
                            </h4>
                            <img src={toggleFilter ? require("../../../Assets/Image/filter-icon-white.svg").default : require("../../../Assets/Image/filter-icon.svg").default}
                                draggable={false}
                                className={`${toggleFilter ? `e-bg-indigo` : ``} transition e-border-1px-anti-flash-white  e-border-radius-8 p-2 cursor-pointer`}
                                alt='filter'
                                onClick={() => setToggleFilter(!toggleFilter)} />
                        </div>

                        {/* filter */}
                        {toggleFilter &&
                            <div className='e-bg-anti-flash-white-dark py-3 px-4 mt-2'>
                                <div className='row'>
                                    <div className='col-xl-2 col-lg-2 col-md-3 col-sm-4 col-6'>
                                        <CustomSelectBox label="Filter by"
                                            value={selected}
                                            options={USER_REG_FILTER}
                                            type={2}
                                            className="e-bg-white e-border-radius-8 e-table-select"
                                            isSearchable={false}
                                            selectChange={(value) => _handleFilterRegister(value)}
                                        />
                                    </div>

                                    {/* City filter */}
                                    <div className='col-xl-2 col-lg-2 col-md-3 col-sm-4 col-6'>
                                        <CustomSelectBox label="City"
                                            value={selectedCity}
                                            options={props.cityData}
                                            type={2}
                                            className="e-bg-white e-border-radius-8 e-table-select"
                                            isSearchable={false}
                                            selectChange={(value) => _handleFilterCity(value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        }

                        {/* Data table */}
                        {userRegisteredData.data.length !== 0 ?
                            <div className='e-table'>
                                <DataTableComponent
                                    key={`user-registered-${currentPage}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                    columns={TABLE_COLUMNS}
                                    pagination={true}
                                    data={userRegisteredData.data}
                                    paginationServer={true}
                                    paginationPerPage={ITEMS_PER_PAGE}
                                    paginationTotalRows={userRegisteredData.total_count}
                                    paginationDefaultPage={currentPage}
                                    paginationResetDefaultPage={false}
                                    sortServer={true}
                                    defaultSortFieldId={sortField}
                                    defaultSortAsc={sortDirection !== 'desc'}
                                    onSort={_handleSort}
                                    onChangePage={_handlePageChange} />
                            </div>
                            :
                            // empty screen
                            <div className='h-100vh pt-5 mt-5'>
                                <EmptyScreen message="No user found" />
                            </div>
                        }
                    </div>
                </>
            }
        </>
    )
}

export default UserRegistered;
